import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { Helmet } from 'react-helmet-async';
import './About.css';

const whyChooseUs = [
    {
        icon: "ri-focus-3-line",
        title: "Strategic Alignment",
        desc: "We align brand objectives with creators whose audience demographics and content style match perfectly."
    },
    {
        icon: "ri-loop-right-line",
        title: "End-to-End Management",
        desc: "From onboarding to final deliverables, we manage the entire lifecycle so you don't have to."
    },
    {
        icon: "ri-chat-smile-3-line",
        title: "Clear Communication",
        desc: "Standardized processes and open channels minimize friction and ensure timely execution."
    }
];

const brandBenefits = [
    "Scale influencer marketing without operational burden",
    "Access curated creator networks",
    "Campaign monitoring and tracking",
    "Consistent quality control",
    "No individual outreach needed"
];

const creatorBenefits = [
    "Access to genuine brand collaborations",
    "Clear expectations and guidelines",
    "Fair opportunities and compensation",
    "Professional coordination and support",
    "Focus on content creation, not negotiations"
];

const values = [
    { icon: "ri-trophy-line", title: "Professionalism", desc: "Highest standards in every interaction." },
    { icon: "ri-hand-heart-line", title: "Accountability", desc: "Taking responsibility for results." },
    { icon: "ri-line-chart-line", title: "Measurable Impact", desc: "Data-driven results that matter." },
    { icon: "ri-plant-line", title: "Long-Term Growth", desc: "Building sustainable partnerships." }
];

const About = ({ user }) => {
    const navigate = useNavigate();

    useEffect(() => {
        // window.scrollTo(0, 0);
    }, []);

    const scrollRef = React.useRef(null);
    const isPausedRef = React.useRef(false);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        const autoScroll = () => {
            if (window.innerWidth <= 768 && !isPausedRef.current) {
                const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth;
                // Use actual card width calculation if possible, but approximation is fine for now
                // or just scroll by clientWidth for full page? User said "auto slider", smooth continuous is also an option.
                // 300px is card width + 1.5rem gap (24px) = 324px ~
                const cardWidth = 324;

                if (scrollContainer.scrollLeft >= maxScrollLeft - 10) {
                    scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    scrollContainer.scrollBy({ left: cardWidth, behavior: 'smooth' });
                }
            }
        };

        const intervalId = setInterval(autoScroll, 3000);

        const handlePause = () => { isPausedRef.current = true; };
        const handleResume = () => { isPausedRef.current = false; };

        scrollContainer.addEventListener('mouseenter', handlePause);
        scrollContainer.addEventListener('mouseleave', handleResume);
        scrollContainer.addEventListener('touchstart', handlePause);
        scrollContainer.addEventListener('touchend', handleResume);

        return () => {
            clearInterval(intervalId);
            scrollContainer.removeEventListener('mouseenter', handlePause);
            scrollContainer.removeEventListener('mouseleave', handleResume);
            scrollContainer.removeEventListener('touchstart', handlePause);
            scrollContainer.removeEventListener('touchend', handleResume);
        };
    }, []);

    return (
        <div className="about-page">
            <Helmet>
                <title>About Us - Collaboration World</title>
                <meta name="description" content="Learn more about Collaboration World. We are building the bridge between top-tier brands and content creators for strategic influencer marketing." />
                <link rel="canonical" href="https://www.collaborationworld.in/about" />
            </Helmet>

            <Navbar user={user} />

            {/* Hero Section */}
            <header className="about-hero">
                <div className="hero-content">
                    <span className="subtitle">Who We Are</span>
                    <h1>Bridging Brands &<br />Creators</h1>
                    <p>Collaboration World is the ecosystem where meaningful partnerships drive real growth.</p>
                </div>
            </header>

            {/* Mission Section */}
            <section className="mission-section">
                <div className="mission-content">
                    <h2>Our Mission</h2>
                    <p>
                        To connect brands with the right creators in a structured, transparent, and results-oriented manner.
                        We act as the strategic bridge, enabling mutually beneficial partnerships that drive awareness,
                        engagement, and conversions.
                    </p>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="why-us-section">
                <div className="section-header">
                    <h2>What We Do</h2>
                </div>
                <div className="features-grid" ref={scrollRef}>
                    {whyChooseUs.map((item, index) => (
                        <div key={index} className="feature-card">
                            <div className="icon-wrapper"><i className={item.icon}></i></div>
                            <h3>{item.title}</h3>
                            <p>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* For Brands (Sticky) */}
            <section className="split-section">
                <div className="split-content">
                    <span className="section-label">For Brands</span>
                    <h2>Scale with Confidence</h2>
                    <p className="split-desc">
                        We handle the heavy lifting of influencer marketing, allowing you to focus on your core business while we amplify your reach.
                    </p>
                    <ul className="benefit-list">
                        {brandBenefits.map((item, index) => (
                            <li key={index}><i className="ri-check-line"></i> {item}</li>
                        ))}
                    </ul>
                </div>
                <div className="split-visual-wrapper">
                    <div className="sticky-image">
                        <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800" alt="For Brands" />
                        <div className="image-overlay"></div>
                    </div>
                </div>
            </section>

            {/* For Creators (Sticky - Reversed) */}
            <section className="split-section reverse">
                <div className="split-content">
                    <span className="section-label">For Creators</span>
                    <h2>Create Without Limits</h2>
                    <p className="split-desc">
                        We manage the logistics and negotiations so you can do what you do best: create amazing content that resonates.
                    </p>
                    <ul className="benefit-list">
                        {creatorBenefits.map((item, index) => (
                            <li key={index}><i className="ri-check-line"></i> {item}</li>
                        ))}
                    </ul>
                </div>
                <div className="split-visual-wrapper">
                    <div className="sticky-image">
                        <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800" alt="For Creators" />
                        <div className="image-overlay"></div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="values-section">
                <div className="section-header">
                    <h2>Our Core Values</h2>
                </div>
                <div className="values-grid">
                    {values.map((val, index) => (
                        <div key={index} className="value-card">
                            <div className="value-icon"><i className={val.icon}></i></div>
                            <h3>{val.title}</h3>
                            <p>{val.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="about-cta">
                <h2>Ready to Start Your Journey?</h2>
                <p>Join Collaboration World and experience the future of influencer marketing.</p>
                <button onClick={() => navigate('/contact')} className="btn-cta-large">
                    Get Started Today
                </button>
            </section>

            <Footer />
        </div>
    );
};

export default About;
